// @flow
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet';
import { Box } from 'grommet';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { type Map } from 'esm-web-pro/containers/map/types';
import UseHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import CableList from 'esm-web-pro/components/pages/network/CableList';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { numberPointsCallbacksPipe } from 'esm-web-pro/containers/pages/network/objects/pipe/Pipe';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';

export type VisualisedPipe = {
    id: number,
    uuid: string,
    color: string,
    start_termination_point: Object,
    end_termination_point: Object,
    start_container_point: Object,
    end_container_point: Object,
    path?: Array<[number, number]>
}

export type VisualisedContinuousPipe = {
    id: number,
    uuid: string,
    color: string,
    path?: Array<[number, number]>,
    start_point: Object,
    end_point: Object,
}

type Props = {
    line: Object,
    intl: IntlShape,
    history: Object,
    match: Object,
    editor: BaseEditor,
    leaflet: LeafletContext,
    clicked: string,
    setClicked: Function,
    getPipePath: (id: string, map: Map) => Promise<VisualisedPipe>,
    getContinuousPipePath: (id: string, map: Map) => Promise<VisualisedContinuousPipe>,
    map: Map,
}

const GisLineContent = (props: Props) => {
    const {
        line, intl, editor, leaflet, clicked, setClicked, getPipePath, getContinuousPipePath, map,
    } = props;
    const { air_route: airRoute, pipes, virtual_pipe: virtualPipe } = line;

    const highlightPipe = async (pipe) => {
        editor.unregisterAllLoadingCallback();
        let vPipe;
        let pointCallbacks;

        if (pipe.continuous_pipe) {
            vPipe = await getContinuousPipePath(pipe.continuous_pipe.uuid, map);
            let startNumber = vPipe.start_point.sequence;
            if (vPipe.start_point?.point?.type === 'pop' && vPipe.start_point.alias) {
                startNumber = vPipe.start_point.alias;
            }
            let endNumber = vPipe.end_point.sequence;
            if (vPipe.end_point?.point?.type === 'pop' && vPipe.end_point.alias) {
                endNumber = vPipe.end_point.alias;
            }
            pointCallbacks = [
                UseHighlightPoint(vPipe.start_point.coordinates.coordinates, startNumber),
                UseHighlightPoint(vPipe.end_point.coordinates.coordinates, endNumber),
            ];
        } else {
            vPipe = await getPipePath(pipe.identity, map);
            pointCallbacks = numberPointsCallbacksPipe(vPipe);
        }

        const coordinates = vPipe.path || line.path.coordinates;
        const pipeCoords = coordinates.map((c) => [c[1], c[0]]);
        const highlightPipeCallback = UseHighlightLine(pipeCoords,
            [{
                color: vPipe.color, dashArray: '20', className: 'route-line',
            },
            {
                color: vPipe.color, dashArray: null, className: '',
            }]);
        if (leaflet.map) {
            editor.registerLoadingCallback(highlightPipeCallback);
            editor.registerMultipleLoadingCallbacks(pointCallbacks);

            leaflet.map.highlightedGeometry = {
                bounds: pipeCoords, center: null,
            };
            leaflet.map.panInsideBounds(pipeCoords);
        }
        editor.reloadFeatures();
        const rowId = `Pipe ${pipe.id}`;
        setClicked(rowId);
    };

    return (
        <Box pad={{ vertical: '20px' }} height={{ min: '100%' }}>
            {!isEmpty(airRoute) && (
            <>
                <ContentLabel>
                    <FormattedMessage
                        id="airroute-content-title"
                        defaultMessage="Air Route"
                    />
                </ContentLabel>
                <Box pad={{ bottom: 'xsmall' }}>
                    {!isEmpty(airRoute[0].cables) && (
                        <CableList
                            padLevel={2}
                            cables={airRoute[0].cables}
                            editor={editor}
                            clicked={clicked}
                            setClicked={setClicked}
                            map={map}
                        />
                    )}
                </Box>
            </>
            )}
            {(!isEmpty(virtualPipe) && !isEmpty(virtualPipe.cables)) && (
            <>
                <ContentLabel>
                    <FormattedMessage
                        id="route-underground-no-pipe-title"
                        defaultMessage="Underground No Pipe"
                    />
                </ContentLabel>
                <Box pad={{ bottom: 'xsmall' }}>
                    {!isEmpty(virtualPipe.cables)
                    && (
                        <CableList
                            cables={virtualPipe.cables}
                            padLevel={2}
                            editor={editor}
                            clicked={clicked}
                            setClicked={setClicked}
                            map={map}
                        />
                    )}
                </Box>
            </>
            )}
            {!isEmpty(pipes) && (
                <>
                    <ContentLabel>
                        <FormattedMessage
                            id="pipe-content-title"
                            defaultMessage="Pipes"
                        />
                    </ContentLabel>
                    <Box>
                        {line.pipes.map((pipe) => (
                            <Box key={pipe.uuid} pad={{ bottom: 'xsmall' }}>
                                <ContentRow
                                    padLevel={1}
                                    clickedRowIdentity={clicked}
                                    rowId={`Pipe ${pipe.id}`}
                                    onClick={() => highlightPipe(pipe)}
                                    actions={(
                                        <RowActions
                                            iconPadding="3px"
                                            gapBetweenActions="medium"
                                            actions={[{
                                                key: `${line.id}-${pipe.id}-more`,
                                                icon: <MeatballsIcon />,
                                                size: '20px',
                                                width: '3px',
                                                callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                                    e.stopPropagation();
                                                    const path = props.match.url.split('/layer');
                                                    props.history.push(`${path[0]}/layer/${pipe.continuous_pipe
                                                        ? 'continuousPipe' : 'pipe'}/${pipe.continuous_pipe?.identity
                                                    || pipe.identity}`);
                                                },
                                                tooltip: intl.formatMessage(actionMessages.more),
                                            }]}
                                        />
                                    )}
                                >
                                    <ValueText>
                                        <FormattedMessage
                                            id="route-pipe-title"
                                            defaultMessage="Pipe {pipeid}"
                                            values={{
                                                pipeid: pipe.continuous_pipe?.ref || pipe.ref,
                                            }}
                                        />
                                    </ValueText>
                                </ContentRow>
                                <Box>
                                    <CableList
                                        padLevel={2}
                                        cables={pipe.cables}
                                        editor={editor}
                                        clicked={clicked}
                                        setClicked={setClicked}
                                        map={map}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export const pipePathApiCallbacks = (apiClient: ApiClient) => ({
    getPipePath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources['pipe.geo']
                    .path(map.uuid, id, { is_draft: map.uuid });
            }
            return apiClient.resources['pipe.geo']
                .path(map.uuid, id);
        },
    },
    getContinuousPipePath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources.continuousPipe
                    .path(id, { is_draft: map.uuid });
            }
            return apiClient.resources.continuousPipe
                .path(id);
        },
    },
});

export default injectIntl(withLeaflet(withRouter(React.memo<Props>(
    connectAPI(pipePathApiCallbacks)(GisLineContent),
))));
